import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AppShell from '../components/layout/AppShell';
import { useUserProfile } from '../hooks/useUserProfile';
import { useStudyPlans } from '../hooks/useStudyPlans';
import useStudentProgress from '../hooks/useStudentProgress';
import { formatUsd } from '../data/studyPlans';
import { SBA } from '../theme';
import { getAppNavItems } from '../utils/appNavItems';
import { subscribeStudyTracking, formatDuration, computeStudyStreak, weeklyStudyPercents } from '../utils/studyTracking';

export default function Dashboard() {
  const { currentUser, isAdmin, hasSubscription, subscriptionInfo } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { profile } = useUserProfile(currentUser?.uid);
  const { plans, extensions } = useStudyPlans();
  const progress = useStudentProgress(currentUser);
  const [tracking, setTracking] = useState(null);

  useEffect(() => {
    if (!currentUser?.uid) return undefined;
    const unsubTrack = subscribeStudyTracking(currentUser.uid, setTracking);
    return () => unsubTrack();
  }, [currentUser?.uid]);

  const unlocked = Boolean(isAdmin || hasSubscription);
  const showWelcome = searchParams.get('welcome') === '1';
  const showExtended = searchParams.get('extended') === '1';
  const [welcomeDismissed, setWelcomeDismissed] = useState(false);

  const displayName = currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Student';
  const firstName = profile?.firstName || displayName.split(' ')[0];
  const navItems = getAppNavItems(isAdmin, location.pathname, { hasSubscription });

  const activePlan = useMemo(() => {
    const planId = subscriptionInfo?.planId;
    return plans.find((p) => p.id === planId) || null;
  }, [plans, subscriptionInfo]);

  const streak = computeStudyStreak(tracking?.dailyLogs);
  const hoursLabel = formatDuration(tracking?.totalActiveSeconds || 0);
  const weekly = weeklyStudyPercents(tracking?.dailyLogs);
  const coursePct = unlocked ? progress.overallPct || 0 : 0;

  const profileIncomplete = Boolean(profile) && profile.profileComplete !== true && !isAdmin;

  function dismissWelcome() {
    setWelcomeDismissed(true);
    setSearchParams({}, { replace: true });
  }

  return (
    <AppShell navItems={navItems}>
      {/* ---------- Welcome screen after payment ---------- */}
      {unlocked && (showWelcome || showExtended) && !welcomeDismissed ? (
        <div style={s.welcomeCard}>
          <button type="button" style={s.welcomeClose} onClick={dismissWelcome} aria-label="Dismiss">
            <span className="material-symbols-outlined" style={{ fontSize: 20 }}>close</span>
          </button>
          <div style={s.welcomeIcon}>
            <span className="material-symbols-outlined" style={{ fontSize: 34, color: '#1a1405' }}>
              {showExtended ? 'more_time' : 'celebration'}
            </span>
          </div>
          {showExtended ? (
            <>
              <h1 style={s.welcomeTitle}>Extension activated</h1>
              <p style={s.welcomeSub}>
                Your extra days were added successfully.
                {subscriptionInfo?.daysLeft != null
                  ? ` You now have ${subscriptionInfo.daysLeft} days of access remaining.`
                  : ''}
              </p>
            </>
          ) : (
            <>
              <h1 style={s.welcomeTitle}>
                Welcome {firstName} to the Success Bridge Academy
              </h1>
              <p style={s.welcomeSub}>
                You chose the{' '}
                <strong>{subscriptionInfo?.planLabel || 'GED AI study plan'}</strong>. Your plan
                includes:
              </p>
              <ul style={s.welcomeList}>
                {(activePlan?.features?.length
                  ? activePlan.features
                  : [
                      'Full GED course content',
                      'Course explanation by AI',
                      'GED AI tutor — 24/7',
                      'System available 24/7',
                      'AI quizzes system',
                      'AI practice exams system',
                    ]
                ).map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
              <p style={s.welcomeValue}>
                The GED final exam is also AI-based — preparing with our AI system makes your
                preparation 100% so you clear your GED in the first attempt.
              </p>
            </>
          )}
          <button type="button" style={s.welcomeBtn} onClick={dismissWelcome}>
            Start studying
          </button>
        </div>
      ) : null}

      {/* ---------- Complete your profile (blinking) ---------- */}
      {profileIncomplete ? (
        <div className="sba-blink" style={s.profileBanner} onClick={() => navigate('/profile')}>
          <span className="material-symbols-outlined" style={{ fontSize: 22, color: SBA.goldStrong }}>
            badge
          </span>
          <p style={s.profileBannerText}>
            <strong>Complete your profile first</strong> — add your CNIC / passport number,
            education details, and guardian information.
          </p>
          <span style={s.profileBannerBtn}>Complete now</span>
        </div>
      ) : null}

      {!unlocked ? (
        <div className="sba-home">
          <section className="sba-welcome">
            <h1>Welcome, {firstName}</h1>
            <p>Choose a plan to unlock lessons, the AI tutor, and practice exams.</p>
            <button type="button" className="sba-btn-gold" onClick={() => navigate('/billing')}>
              Choose a plan
            </button>
          </section>
          <PortalStats streak={0} hoursLabel="0m" coursePct={0} />
          <StudyChart percents={[0, 0, 0, 0, 0, 0]} />
        </div>
      ) : (
        <div className="sba-home">
          <section className="sba-welcome">
            <h1>Welcome back, {firstName}</h1>
            <p>You can pick up the next lesson whenever you are ready.</p>
            <button type="button" className="sba-btn-gold" onClick={() => navigate('/course')}>
              Continue lesson
            </button>
          </section>
          <PortalStats streak={streak} hoursLabel={hoursLabel} coursePct={coursePct} />
          <StudyChart percents={weekly} />
          <section className="sba-panel">
            <h2>Your subjects</h2>
            {progress.subjects.map(({ label, pct = 0, id }) => (
              <div key={id || label} className="sba-subject-row">
                <span>{label}</span>
                <div className="sba-bar" aria-hidden="true">
                  <i style={{ width: `${pct}%` }} />
                </div>
              </div>
            ))}
          </section>
          {!isAdmin && extensions.length > 0 ? (
            <div style={s.extCard}>
              <div style={s.extHead}>
                <div>
                  <h3 style={s.extTitle}>Need more days?</h3>
                  <p style={s.extSub}>
                    {subscriptionInfo?.daysLeft != null
                      ? `${subscriptionInfo.daysLeft} days left on your plan.`
                      : 'Extend your subscription anytime.'}
                  </p>
                </div>
              </div>
              <ExtensionPicker
                extensions={extensions}
                onContinue={(days) => navigate('/billing', { state: { extendDays: days } })}
              />
            </div>
          ) : null}
        </div>
      )}
    </AppShell>
  );
}

function PortalStats({ streak, hoursLabel, coursePct }) {
  return (
    <div className="sba-stat-grid">
      <article className="sba-stat">
        <div className="sba-stat-n">{streak}</div>
        <div className="sba-stat-l">Day streak</div>
      </article>
      <article className="sba-stat">
        <div className="sba-stat-n">{hoursLabel}</div>
        <div className="sba-stat-l">Studied</div>
      </article>
      <article className="sba-stat">
        <div className="sba-stat-n">{coursePct}%</div>
        <div className="sba-stat-l">Course progress</div>
        <div className="sba-bar" aria-hidden="true">
          <i style={{ width: `${coursePct}%` }} />
        </div>
      </article>
    </div>
  );
}

function StudyChart({ percents }) {
  const values = percents?.length ? percents : [0, 0, 0, 0, 0, 0];
  const width = 600;
  const height = 140;
  const max = Math.max(1, ...values);
  const step = width / Math.max(1, values.length - 1);
  const points = values.map((value, index) => {
    const x = index * step;
    const y = height - 16 - (value / max) * (height - 32);
    return `${x} ${y}`;
  });
  const line = `M${points.join(' L')}`;
  const area = `${line} L${width} ${height} L0 ${height} Z`;

  return (
    <section className="sba-panel">
      <h2>Study time</h2>
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" role="img" aria-label="Study time over recent weeks">
        <defs>
          <linearGradient id="studyGoldFade" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#C5A046" stopOpacity="0.45" />
            <stop offset="1" stopColor="#C5A046" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill="url(#studyGoldFade)" />
        <path d={line} fill="none" stroke="#1B365D" strokeWidth="2.5" />
      </svg>
    </section>
  );
}

function ExtensionPicker({ extensions, onContinue }) {
  const [days, setDays] = useState('');
  const selected = extensions.find((o) => String(o.days) === String(days));
  return (
    <div style={s.extPickRow}>
      <select value={days} onChange={(e) => setDays(e.target.value)} style={s.extSelect}>
        <option value="">Select extra days</option>
        {extensions.map((o) => (
          <option key={o.days} value={o.days}>
            {o.days} days more — {formatUsd(o.price)}
          </option>
        ))}
      </select>
      <button
        type="button"
        disabled={!selected}
        style={s.extGoBtn(!selected)}
        onClick={() => selected && onContinue(selected.days)}
      >
        Continue to payment gate
      </button>
    </div>
  );
}

const s = {
  welcomeCard: {
    position: 'relative',
    background: `linear-gradient(150deg, ${SBA.navy} 0%, ${SBA.navyDeep} 65%)`,
    color: '#fff',
    borderRadius: 22,
    padding: '30px 28px',
    marginBottom: 28,
    boxShadow: '0 18px 48px rgba(15,33,55,0.35)',
  },
  welcomeClose: {
    position: 'absolute',
    top: 14,
    right: 14,
    border: 'none',
    background: 'rgba(255,255,255,0.12)',
    color: '#fff',
    width: 34,
    height: 34,
    borderRadius: 10,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  welcomeIcon: {
    width: 64,
    height: 64,
    borderRadius: 18,
    background: SBA.gold,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
    boxShadow: '0 10px 26px rgba(197,160,70,0.4)',
  },
  welcomeTitle: {
    margin: '0 0 8px',
    fontFamily: SBA.fontDisplay,
    fontSize: 26,
    fontWeight: 800,
    letterSpacing: '-0.02em',
  },
  welcomeSub: { margin: '0 0 14px', fontSize: 15, opacity: 0.92, lineHeight: 1.5 },
  welcomeList: {
    margin: '0 0 14px',
    paddingLeft: 20,
    fontSize: 14,
    lineHeight: 1.9,
    columnCount: 2,
    columnGap: 32,
    maxWidth: 640,
  },
  welcomeValue: {
    margin: '0 0 18px',
    fontSize: 13,
    color: SBA.gold,
    fontWeight: 600,
    lineHeight: 1.55,
    maxWidth: 640,
  },
  welcomeBtn: {
    border: 'none',
    background: SBA.gold,
    color: '#1a1405',
    fontWeight: 800,
    borderRadius: 12,
    padding: '13px 22px',
    fontSize: 14,
    cursor: 'pointer',
  },
  profileBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
    padding: '14px 18px',
    borderRadius: 16,
    background: 'rgba(197,160,70,0.1)',
    border: '1.5px solid rgba(197,160,70,0.5)',
    marginBottom: 24,
    cursor: 'pointer',
  },
  profileBannerText: { margin: 0, flex: 1, fontSize: 13, color: SBA.navy, minWidth: 220 },
  profileBannerBtn: {
    background: SBA.navy,
    color: '#fff',
    borderRadius: 10,
    padding: '9px 14px',
    fontWeight: 700,
    fontSize: 12,
    whiteSpace: 'nowrap',
  },
  lockBanner: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 16,
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: 24,
    padding: '20px 22px',
    borderRadius: 18,
    background: `linear-gradient(135deg, ${SBA.navy} 0%, ${SBA.navyDeep} 55%, ${SBA.tertiary} 100%)`,
    color: '#fff',
  },
  lockEyebrow: {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: SBA.gold,
    marginBottom: 6,
  },
  lockTitle: { margin: '0 0 6px', fontSize: 20, fontWeight: 800 },
  lockSub: { margin: 0, fontSize: 13, lineHeight: 1.5, opacity: 0.9, maxWidth: 620 },
  unlockBtn: {
    border: 'none',
    background: SBA.gold,
    color: '#1a1405',
    fontWeight: 800,
    borderRadius: 12,
    padding: '12px 18px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  greetingBlock: { marginBottom: 32 },
  regChip: {
    display: 'inline-block',
    margin: '0 0 10px',
    padding: '6px 12px',
    borderRadius: 999,
    background: '#eef2ff',
    border: '1px solid #c7d2fe',
    fontSize: 12,
    color: '#1b365d',
  },
  greeting: {
    fontFamily: SBA.fontDisplay,
    fontSize: 24,
    fontWeight: 800,
    color: SBA.navy,
    letterSpacing: '-0.02em',
    marginBottom: 4,
  },
  greetingSub: { fontSize: 14, color: SBA.onSurfaceVariant },
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 36 },
  statCard: {
    background: '#fff',
    borderRadius: 24,
    padding: 20,
    border: '1px solid rgba(226,232,240,0.9)',
    boxShadow: '0 4px 24px rgba(27,54,93,0.06)',
  },
  statIcon: {
    width: 44,
    height: 44,
    borderRadius: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  },
  statValue: {
    fontFamily: SBA.fontDisplay,
    fontSize: 24,
    fontWeight: 800,
    color: SBA.navy,
    marginBottom: 2,
  },
  statLabel: { fontSize: 12, color: SBA.outline, fontWeight: 500 },
  sectionTitle: {
    fontFamily: SBA.fontDisplay,
    fontSize: 18,
    fontWeight: 800,
    color: SBA.navy,
    marginBottom: 16,
  },
  courseCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    background: '#fff',
    borderRadius: 20,
    padding: '18px 20px',
    border: '1px solid #e2e8f0',
    marginBottom: 28,
    boxShadow: '0 8px 24px rgba(27,54,93,0.06)',
    cursor: 'pointer',
  },
  courseCardLeft: { display: 'flex', alignItems: 'center', gap: 14 },
  courseCardIcon: {
    width: 52,
    height: 52,
    borderRadius: 16,
    background: '#eef2f7',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  courseCardTitle: { fontWeight: 800, color: SBA.navy, marginBottom: 2 },
  courseCardSub: { fontSize: 13, color: SBA.onSurfaceVariant },
  subjectsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 16,
    marginBottom: 28,
  },
  subjectCard: {
    background: '#fff',
    borderRadius: 20,
    padding: 18,
    border: '1px solid #e2e8f0',
  },
  subjectIcon: {
    width: 48,
    height: 48,
    borderRadius: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  },
  subjectLabel: { fontWeight: 800, color: SBA.navy, marginBottom: 10 },
  progressTrack: {
    height: 8,
    borderRadius: 999,
    background: '#e2e8f0',
    overflow: 'hidden',
    marginBottom: 8,
  },
  progressFill: { height: '100%', borderRadius: 999 },
  progressText: { fontSize: 12, color: SBA.outline, marginBottom: 12 },
  startBtn: {
    width: '100%',
    background: '#fff',
    border: '1.5px solid',
    borderRadius: 12,
    padding: '10px 12px',
    fontWeight: 700,
    cursor: 'pointer',
  },
  plansRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: 16,
  },
  planCta: {
    background: '#fff',
    borderRadius: 20,
    padding: 20,
    border: '1px solid #e2e8f0',
    boxShadow: '0 4px 20px rgba(27,54,93,0.05)',
    position: 'relative',
  },
  planBadge: {
    position: 'absolute',
    top: 14,
    right: 14,
    background: SBA.gold,
    color: '#1a1405',
    fontSize: 10,
    fontWeight: 800,
    padding: '3px 9px',
    borderRadius: 999,
  },
  planCtaName: { margin: '0 0 8px', fontWeight: 800, color: SBA.navy, fontSize: 15 },
  planCtaPrice: {
    margin: '0 0 2px',
    fontSize: 26,
    fontWeight: 800,
    color: SBA.navy,
    display: 'flex',
    alignItems: 'baseline',
    gap: 8,
  },
  planCtaWas: { fontSize: 13, color: '#94a3b8', textDecoration: 'line-through', fontWeight: 600 },
  planCtaDuration: { margin: '0 0 14px', fontSize: 12, color: SBA.outline },
  planCtaBtn: {
    width: '100%',
    border: 'none',
    background: SBA.navy,
    color: '#fff',
    borderRadius: 12,
    padding: '11px 14px',
    fontWeight: 800,
    fontSize: 13,
    cursor: 'pointer',
  },
  bottomGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: 16,
    marginBottom: 16,
  },
  extCard: {
    background: '#fff',
    borderRadius: 20,
    border: '1px solid #e2e8f0',
    boxShadow: '0 4px 20px rgba(27,54,93,0.05)',
    padding: 18,
    alignSelf: 'start',
  },
  extHead: { display: 'flex', gap: 12, alignItems: 'center', marginBottom: 14 },
  extIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    background: SBA.navy,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  extTitle: { margin: 0, fontSize: 15, fontWeight: 800, color: SBA.navy },
  extSub: { margin: 0, fontSize: 12, color: SBA.outline },
  extPickRow: { display: 'grid', gap: 10 },
  extSelect: {
    padding: '12px 14px',
    borderRadius: 12,
    border: '1.5px solid #e2e8f0',
    fontSize: 14,
    background: '#fff',
    width: '100%',
  },
  extGoBtn: (disabled) => ({
    padding: '12px 16px',
    borderRadius: 12,
    border: 'none',
    background: disabled ? '#e4e0d6' : '#1b365d',
    color: disabled ? '#4a5a70' : '#fff',
    fontWeight: 800,
    fontSize: 13,
    cursor: disabled ? 'not-allowed' : 'pointer',
  }),
};
